import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { fmtLocalDate } from '../../utils/datetime';

export interface RangeOpt {
  label: string;
  days: number | null;
  /** Long name for the `combo` variant ("Last 3 months"), where `label` becomes
   *  the short code on the right. Ignored by `dropdown` and `chips`. */
  name?: string;
}

/** A custom window as calendar days (`YYYY-MM-DD`, inclusive). Either end may
 *  be open. Compared as text, like the presets' cut-off, so a boundary never
 *  shifts across midnight for someone west of the server. */
export interface CustomRange { from: string | null; to: string | null; }

/** The words for the active window — the preset's name, or a custom span as
 *  `MM/DD/YYYY – MM/DD/YYYY` via the shared date helper (never inline, §14b.6).
 *  Exported so a page's subtitle and the picker can never say different things. */
export function describeRange(
  ranges: RangeOpt[], value: number | null, custom?: CustomRange | null,
): string {
  if (custom) {
    const { from, to } = custom;
    if (from && to) return `${fmtLocalDate(from)} – ${fmtLocalDate(to)}`;
    if (from) return `From ${fmtLocalDate(from)}`;
    if (to) return `Up to ${fmtLocalDate(to)}`;
    return 'Custom range';
  }
  const r = ranges.find((x) => x.days === value) ?? ranges[ranges.length - 1];
  return r?.name ?? r?.label ?? 'All';
}

/** BO UI Standard §17 canonical chip set for Tracking forms — 3M is the default.
 *  Mirrors the global standard DATE_OPTS (components/listDefaults.ts) + GoLive:
 *  1M · 3M · 6M · 1Y · 2Y · 5Y · All. */
export const TRACKING_RANGES: RangeOpt[] = [
  { label: '1M', days: 30 }, { label: '3M', days: 90 }, { label: '6M', days: 180 },
  { label: '1Y', days: 365 }, { label: '2Y', days: 730 }, { label: '5Y', days: 1825 },
  { label: 'All', days: null },
];

interface Props {
  ranges: RangeOpt[];
  value: number | null;
  onChange: (days: number | null) => void;
  /** 'dropdown' (default) = the compact popup; 'chips' = the §17 Tracking-form
   *  chip row (1M · 3M · 6M · 1Y · All, active = theme orange, applies on click).
   *  'field' (opt-in, 2026-11-03) = the G5 toolbar combo: the caption as plain
   *  text, then a boxed combo with the value and a drop arrow; keyboard-driven
   *  (closed ↑/↓ change the value, Alt+↓ / Enter / Space open, ↑/↓ + Enter pick,
   *  Esc closes). First used by Reagent Preparation. */
  variant?: 'dropdown' | 'chips' | 'combo' | 'field';
  /** Optional inline label rendered before the chips (e.g. "Date Range"). Only the
   *  chip variant uses it — a self-contained, aligned row (no external wrapper). */
  label?: string;
  /** `combo` only (prototype v68's range combo — opt-in, so the ~25 forms on
   *  `dropdown` are untouched): the active custom window, or `null` when a
   *  preset is active. Passing `onCustomChange` adds "Custom range…" with
   *  From / To pickers. Picking a preset clears it back to `null`. */
  custom?: CustomRange | null;
  onCustomChange?: (c: CustomRange | null) => void;
}

/**
 * Custom Date Range dropdown with an ORANGE selection/hover list. A native
 * <select> can't restyle its option highlight (it showed browser-blue), so this
 * renders its own popup. Picking a value only updates the draft — the caller
 * commits it on Retrieve / OK, so selecting a range does not auto-fetch.
 */
export function DateRangeSelect({
  ranges, value, onChange, variant = 'dropdown', label, custom = null, onCustomChange,
}: Props) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });
  const btnRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  /** `field` variant: the keyboard cursor in the open list. */
  const [cursor, setCursor] = useState(-1);
  const current = ranges.find((r) => r.days === value) ?? ranges[ranges.length - 1];

  // §17 chip row for Tracking forms — active chip = theme orange, applies on click.
  if (variant === 'chips') {
    return (
      <div className="dr-chips" role="group" aria-label="Date range">
        {label && <span className="dr-chips-lbl">{label}</span>}
        <span className="dr-chips-ico" aria-hidden="true">📅</span>
        {ranges.map((r) => (
          <button key={r.label} type="button"
            className={`dr-chip${r.days === value ? ' on' : ''}`}
            aria-pressed={r.days === value}
            onClick={() => onChange(r.days)}>
            {r.label}
          </button>
        ))}
      </div>
    );
  }

  const toggle = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (r) setPos({ top: r.bottom + window.scrollY + 2, left: r.left + window.scrollX, width: r.width });
    setOpen((o) => !o);
  };

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (popRef.current && !popRef.current.contains(e.target as Node)
          && btnRef.current && !btnRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  // v68 range combo: the window in words, a menu of long name + short code,
  // and an optional custom From / To. Same portal, positioning, outside-click
  // and Escape handling as `dropdown` — only the anatomy differs.
  if (variant === 'combo') {
    const pickPreset = (days: number | null) => {
      onChange(days);
      onCustomChange?.(null);
      setOpen(false);
    };
    return (
      <>
        <button ref={btnRef} type="button" className={`dr-combo${open ? ' open' : ''}`}
                onClick={toggle} aria-haspopup="listbox" aria-expanded={open}>
          <i className="ti ti-calendar-stats" aria-hidden="true" />
          <span className="dr-combo-txt">{describeRange(ranges, value, custom)}</span>
          <i className="ti ti-chevron-down" aria-hidden="true" />
        </button>
        {open && createPortal(
          <div ref={popRef} className="dr-pop dr-cpop" role="listbox"
               style={{ top: pos.top, left: pos.left, minWidth: Math.max(230, pos.width) }}>
            {ranges.map((r) => (
              <button key={r.label} type="button" role="option"
                      aria-selected={!custom && r.days === value}
                      className={`dr-copt${!custom && r.days === value ? ' on' : ''}`}
                      onClick={() => pickPreset(r.days)}>
                <span>{r.name ?? r.label}</span>
                <span className="d">{r.label}</span>
              </button>
            ))}
            {onCustomChange && (
              <button type="button" role="option" aria-selected={!!custom}
                      className={`dr-copt${custom ? ' on' : ''}`}
                      onClick={() => onCustomChange(custom ?? { from: null, to: null })}>
                <span>Custom range…</span>
              </button>
            )}
            {custom && onCustomChange && (
              <div className="dr-cfoot">
                {/* min/max keep From <= To — an inverted window would silently
                    match nothing, which reads as "no data" rather than a typo. */}
                <label>From
                  <input type="date" value={custom.from ?? ''} max={custom.to ?? undefined}
                         onChange={(e) => onCustomChange({ ...custom, from: e.target.value || null })} />
                </label>
                <label>To
                  <input type="date" value={custom.to ?? ''} min={custom.from ?? undefined}
                         onChange={(e) => onCustomChange({ ...custom, to: e.target.value || null })} />
                </label>
                <button type="button" className="ca-btn" onClick={() => setOpen(false)}>Done</button>
              </div>
            )}
          </div>,
          document.body,
        )}
      </>
    );
  }

  // G5 toolbar combo: "Date Filter [ 3M  ▾ ]". Focus stays on the box while the
  // list is open, so the keys below steer it and nothing leaks to the grid.
  if (variant === 'field') {
    const curIdx = Math.max(0, ranges.findIndex((r) => r.days === value));
    const openList = () => { setCursor(curIdx); if (!open) toggle(); };
    const pick = (i: number) => {
      const r = ranges[i];
      if (r) onChange(r.days);
      setOpen(false);
    };
    const onKeyDown = (e: ReactKeyboardEvent<HTMLButtonElement>) => {
      const last = ranges.length - 1;
      if (!open) {
        if ((e.key === 'ArrowDown' && e.altKey) || e.key === 'Enter' || e.key === ' ' || e.key === 'F4') {
          e.preventDefault(); openList();
        } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          // A closed combo changes its value with ↑/↓ (§ ComboBox standard).
          e.preventDefault();
          const next = Math.min(last, Math.max(0, curIdx + (e.key === 'ArrowDown' ? 1 : -1)));
          if (next !== curIdx) onChange(ranges[next]!.days);
        }
        return;
      }
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Home' || e.key === 'End') {
        e.preventDefault(); e.stopPropagation();
        setCursor((c) => e.key === 'Home' ? 0 : e.key === 'End' ? last
          : Math.min(last, Math.max(0, c + (e.key === 'ArrowDown' ? 1 : -1))));
      } else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault(); e.stopPropagation(); pick(cursor < 0 ? curIdx : cursor);
      } else if (e.key === 'Escape') {
        e.preventDefault(); e.stopPropagation(); setOpen(false);
      } else if (e.key === 'Tab') {
        setOpen(false);
      }
    };
    return (
      <span className="dr-field">
        <span className="dr-field-lbl">{label ?? 'Date Range'}</span>
        <button ref={btnRef} type="button" role="combobox" aria-haspopup="listbox" aria-expanded={open}
                // Tooltip = the field's caption ("Date Filter"), as XAF's date-filter action (G5 parity, 2026-11-03).
                aria-label={label ?? 'Date Range'} title={label ?? 'Date Range'} className={`dr-field-box${open ? ' open' : ''}`}
                onClick={() => (open ? setOpen(false) : openList())} onKeyDown={onKeyDown} data-unsaved-ignore>
          <span className="dr-field-val">{current?.label ?? 'All'}</span>
          <span className="dr-field-caret" aria-hidden="true"><i className="ti ti-chevron-down" /></span>
        </button>
        {open && createPortal(
          <div ref={popRef} className="dr-pop dr-fpop" role="listbox"
               style={{ top: pos.top, left: pos.left, minWidth: pos.width }}>
            {ranges.map((r, i) => (
              <div key={r.label} role="option" aria-selected={r.days === value}
                   className={`dr-opt${r.days === value ? ' sel' : ''}${i === cursor ? ' cur' : ''}`}
                   onMouseEnter={() => setCursor(i)}
                   onMouseDown={(e) => e.preventDefault()}   // keep focus on the box
                   onClick={() => pick(i)}>
                {r.label}
              </div>
            ))}
          </div>,
          document.body,
        )}
      </span>
    );
  }

  return (
    <>
      <button ref={btnRef} type="button" className="dr-sel" onClick={toggle}>
        <span className="dr-sel-ico" aria-hidden="true">📅</span>
        <span className="dr-sel-lbl">{label ?? 'Date Range'}</span>
        <span className="dr-sel-val">
          {current?.label ?? 'All'}
          <i className="ti ti-chevron-down" />
        </span>
      </button>
      {open && createPortal(
        <div ref={popRef} className="dr-pop"
          style={{ top: pos.top, left: pos.left, minWidth: Math.max(90, pos.width) }}>
          {ranges.map((r) => (
            <div key={r.label}
              className={`dr-opt${r.days === value ? ' sel' : ''}`}
              onClick={() => { onChange(r.days); setOpen(false); }}>
              {r.label}
            </div>
          ))}
        </div>,
        document.body,
      )}
    </>
  );
}
